import type { DateRange } from "react-day-picker";
import { CategoryFilterKey } from "../components/orders/constants/order.constants";
import { ONE_DAY_MS } from "../constants/date-time.constants";
import { EProductType, NON_SERVICE_PRODUCT_TYPES } from "../constants/product.constants";
import { getEndOfDay, getStartOfDay } from "./date";
import type { IFilterOption } from "../components/shared/quick-filters";
import { isDefined } from "./common";

export enum DateFilterKey {
  YESTERDAY = "yesterday",
  TODAY = "today",
  CUSTOM_DATE = "custom-date",
}

type DateWindow = {
  start: number;
  end: number;
};

export const DATE_FILTER_KEYS = {
  YESTERDAY: DateFilterKey.YESTERDAY,
  TODAY: DateFilterKey.TODAY,
  CUSTOM_DATE: DateFilterKey.CUSTOM_DATE,
} as const;

export const ORDERS_FILTER_OPTIONS: IFilterOption[] = [
  { id: CategoryFilterKey.ALL, label: "All Categories", default: true },
  { id: CategoryFilterKey.WALKING, label: "Walking", default: false },
  { id: CategoryFilterKey.GROOMING, label: "Grooming", default: false },
  { id: CategoryFilterKey.FOOD, label: "Food and Products", default: false },
  { id: DateFilterKey.YESTERDAY, label: "Yesterday", default: false },
  { id: DateFilterKey.TODAY, label: "Today", default: true },
  { id: DateFilterKey.CUSTOM_DATE, label: "Custom Date", default: false },
];

export const CATEGORY_FILTER_IDS = new Set<CategoryFilterKey>([
  CategoryFilterKey.ALL,
  CategoryFilterKey.WALKING,
  CategoryFilterKey.GROOMING,
  CategoryFilterKey.FOOD,
]);

// The FOOD chip ("Food and Products") covers every non-service product type — everything
// except the service categories WALKING and GROOMING — so a product-only order (e.g. an
// accessories-only order) is never invisible to a category filter.
const CATEGORY_TO_SERVICE: Record<string, EProductType[]> = {
  [CategoryFilterKey.WALKING]: [EProductType.WALKING],
  [CategoryFilterKey.GROOMING]: [EProductType.GROOMING],
  [CategoryFilterKey.FOOD]: NON_SERVICE_PRODUCT_TYPES,
};

export function categoryToServiceCategory(
  category: CategoryFilterKey,
): EProductType[] | undefined {
  if (category === CategoryFilterKey.ALL) return undefined;
  return CATEGORY_TO_SERVICE[category];
}

export function getDateWindow(
  activeDateFilter: DateFilterKey,
  customDateRange?: DateRange,
): DateWindow | null {
  const now = new Date();
  const todayStart = getStartOfDay(now).getTime();
  const todayEnd = getEndOfDay(now).getTime();

  if (activeDateFilter === DATE_FILTER_KEYS.TODAY) {
    return { start: todayStart, end: todayEnd };
  }

  if (activeDateFilter === DATE_FILTER_KEYS.YESTERDAY) {
    return { start: todayStart - ONE_DAY_MS, end: todayEnd - ONE_DAY_MS };
  }

  if (isDefined(customDateRange?.from) && isDefined(customDateRange?.to)) {
    return {
      start: getStartOfDay(customDateRange.from).getTime(),
      end: getEndOfDay(customDateRange.to).getTime(),
    };
  }

  return null;
}
